# Manage Enhanced Job Posting drafts

A draft can be saved after the hirer has reviewed their job ad in the Enhanced Job Posting panel.
This draft can then be resumed, queried, previewed, or posted as a job ad to SEEK.

```mermaid Enhanced Job Posting
   sequenceDiagram
   participant Hirer as Hirer
   participant Partner as Partner
   participant Panel as EJP Panel
   participant SEEK API as SEEK API

   Hirer->>Partner: Select Save
   Partner->>Panel: Trigger save event
   Panel->>Partner: Return `draftPositionProfileId`
   opt Resume draft
     Partner->>Panel: Call `render` with `draftPositionProfileId`
   end
   opt Query, preview or post job ad
     Partner->>SEEK API: Call queries or mutations with `draftPositionProfileId` and `idempotencyId`
   end
```

## Trigger save of the draft job ad

When the hirer is ready to post or save progress on their current draft, your software should call `dispatchEvent('seek:draft:save')`.
It returns a Promise to a result object.
`dispatchEvent('seek:draft:save')` does not throw exceptions.

### Result object

| Result property   | Type               | Description                                                                                                      |
| :---------------- | :----------------- | :--------------------------------------------------------------------------------------------------------------- |
| `isError`         | `boolean`          | Whether an error has occurred.                                                                                   |
| `data`            | `object` \| `null` | <p>An object that contains information about the draft job ad.</p><p>Only present when `isError` is `false`.</p> |
| `data.id`         | `string`           | The ID of the saved draft job ad.                                                                                |
| `data.isComplete` | `boolean`          | Whether the draft job ad has all mandatory fields complete and is ready for posting.                             |

If the save succeeded, then `isError` will be `false`, and `data.id` will contain the saved draft’s ID.

If `data.isComplete` is `true`, the draft can be posted as a job ad using the `postPositionFromDraft` mutation.
See [post a draft job ad](/use-cases/job-posting/enhanced/post-a-draft-job-ad) for more information.

If `data.isComplete` is `false`, there are mandatory fields that the hirer has not yet completed.
Attempting to post a draft that is not complete will result in a [`BAD_USER_INPUT`] error from the SEEK API.

[`BAD_USER_INPUT`]: /graphql/error-responses#bad_user_input

`data.id` can be saved alongside the job requisition in your software as a SEEK job ad draft, and used to resume editing later.
If the hirer resumes their posting workflow from a draft, the `id` must be passed as the `draftPositionProfileId` prop to the `render` function.

If there was some problem with saving the draft job ad, then `isError` will be `true`, and `data` will be `null`.
Instructions will be shown within the panel for the hirer to resolve and retry saving.

```javascript
const saveSeekDraftId = async (draftId, isComplete) => {
  // Save the draft ID alongside the job requisition in your software.
};

const handleSubmitButton = async () => {
  const result = await dispatchEvent('seek:questionnaire:save');

  if (result.isError) {
    // No draft ID will be provided if an error occurs.
    // The panel will display an actionable error message to the hirer.
    return;
  }

  await saveSeekDraftId(result.data.id, result.data.isComplete);
};
```

## Resuming a draft

When the hirer resumes their job posting workflow from a saved draft, your software must pass the saved `draftPositionProfileId` as a prop to the `render` function.

```javascript
const props = {
  jobRequisition: {
    // ...
    draftPositionProfileId: savedDraftId // The saved draft ID from your software
  }
};
SeekApi.render(containerNode, 'enhancedJobPosting', props);
```

Note that when the hirer saves changes to a draft job ad, a new draft ID will be returned.
The previous draft ID can be safely discarded.

## Querying draft details

You can retrieve a draft’s details with the `draftPositionProfile` query.
This can be useful if your software displays a summary screen prior to posting the job ad.

```graphql label="Query" lovato-ignore-valid-graphql-operation
query draftPositionProfile($id: String!) {
  draftPositionProfile(id: $id) {
    seekDraftPositionId {
      value
    }
    seekReadyToPostIndicator
    seekDraftPositionOpening {
      postingRequester {
        id {
          value
        }
      }
    }
    positionTitle
    positionOrganizations {
      id {
        value
      }
    }
    seekHirerJobReference
    seekBillingReference
    positionFormattedDescriptions {
      content
    }
    offeredRemunerationPackage {
      basisCode
    }
  }
}
```

```json label="Variables"
{
  "id": "globalPublicTest:positionProfile:draft:seek:2dkY4vZJF"
}
```

```json label="Success result"
{
  "seekDraftPositionOpening": {
    "postingRequester": {
      "id": {
        "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
      },
      "personContacts": [
        {
          "communication": {
            "phone": [],
            "address": [],
            "email": [
              {
                "address": "contact@example.com"
              }
            ]
          },
          "name": {
            "formattedName": "John Doe"
          },
          "roleCode": "Recruiter"
        }
      ]
    },
    "statusCode": "Active"
  },
  "positionTitle": "test title",
  "positionOrganizations": [
    {
      "id": {
        "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
      }
    }
  ],
  "seekAnzWorkTypeCode": "FullTime",
  "positionFormattedDescriptions": [
    {
      "descriptionId": {
        "value": "AdvertisementDetails"
      },
      "content": "test"
    }
  ],
  "positionLocation": [
    {
      "id": {
        "value": "Darwin"
      }
    }
  ],
  "offeredRemunerationPackage": {
    "basisCode": "Salaried",
    "descriptions": ["test description"],
    "ranges": [
      {
        "intervalCode": "Year",
        "minimumAmount": {
          "currency": "USD",
          "value": 5000
        },
        "maximumAmount": {
          "currency": "USD",
          "value": 100000
        }
      }
    ]
  },
  "jobCategories": [
    {
      "id": {
        "value": "category1"
      }
    }
  ],
  "seekDraftPositionId": {
    "value": "globalPublicTest:positionProfile:draft:seek:2dkY4vZJF"
  },
  "seekReadyToPostIndicator": true,
  "postingInstructions": [
    {
      "seekAdvertisementProducts": [
        {
          "id": {
            "value": "SeekAdvertisementProductId"
          }
        }
      ]
    }
  ],
  "seekWorkArrangementCodes": ["OnSite"],
  "seekDraftApplicationQuestionnaire": {
    "components": []
  }
}
```

```json label="Error result"
{
  "errors": [
    {
      "message": "Public test tokens are not accepted when accessing live data",
      "locations": [
        {
          "line": 2,
          "column": 3
        }
      ],
      "path": ["draftPositionProfile"],
      "extensions": {
        "code": "UNAUTHENTICATED"
      }
    }
  ]
}
```

## Previewing a draft

You can preview a draft job ad by calling the `postedPositionProfilePreview` query with the `draftPositionProfileId`.

```graphql lovato-ignore-valid-graphql-operation
query ($draftPositionProfileId: String!) {
  postedPositionProfilePreview(
    draftPositionProfileId: $draftPositionProfileId
  ) {
    previewUri {
      url
    }
  }
}
```

```json label="Variables"
{
  "draftPositionProfileId": "globalPublicTest:positionProfile:draft:2ybRBjb6kqDSNAwfxFcqTd"
}
```

```json label="Success result"
{
  "postedPositionProfilePreview": {
    "previewUri": {
      "url": "https://job-ad-preview.seek.com/anzPublicTest?id=seekAnzPublicTest:postedPositionProfilePreview:seekApi:37EWfWxhwVEGTT5BcjfE2L"
    }
  }
}
```

```json label="Error result"
{
  "errors": [
    {
      "message": "No longer valid; refresh the available ad products for this job ad",
      "extensions": {
        "code": "BAD_USER_INPUT",
        "invalidFields": {
          "/positionProfile/postingInstructions/0/seekAdvertisementProductId": "No longer valid; refresh the available ad products for this job ad"
        }
      }
    }
  ]
}
```

For further details on handling job ad previews, refer to [Previewing a job ad].

[Previewing a job ad]: /use-cases/job-posting/managing-job-ads/previewing-a-job-ad

## Draft lifecycle

A draft job ad is created when the hirer saves their job ad within the Enhanced Job Posting panel.
Each subsequent save event with any changes will return a new draft ID.

A single draft ID can be posted as a SEEK job ad multiple times, or similarly used to resume editing from a specific point in time.
This can allow your system to retain a draft ID as a template to streamline the creation of similar job ads in the future.

Draft job ads are retained for 180 days from their last retrieval.
